import { NgTemplateOutlet } from '@angular/common';
import type { OnInit } from '@angular/core';
import {
  Component,
  computed,
  effect,
  ElementRef,
  forwardRef,
  inject,
  input,
  model,
  Renderer2,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import type { FormValueControl } from '@angular/forms/signals';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { cloneDeep, isArray, isEqual, isString } from 'lodash';
import { distinctUntilChanged, filter, forkJoin } from 'rxjs';
import { ONYX_FOCUSABLE } from '../../../directives/onyx-auto-focus.directive';
import type { OnyxMaybeArray } from '../../../interfaces';
import { OnyxBaseFormControlComponent } from '../../../internal/components/onyx-base-form-control/onyx-base-form-control.component';
import { I18N_NAMESPACE } from '../../../internal/constants';
import { OnyxFileSizePipe } from '../../../pipes/onyx-file-size.pipe';
import { OnyxStorageService } from '../../../services/onyx-storage.service';
import { OnyxButtonComponent } from '../../buttons/onyx-button/onyx-button.component';
import { OnyxIconButtonComponent } from '../../buttons/onyx-icon-button/onyx-icon-button.component';
import type { OnyxError } from '../../error/interfaces';
import { OnyxErrorComponent } from '../../error/onyx-error/onyx-error.component';
import { OnyxIconComponent } from '../../icons/onyx-icon/onyx-icon.component';
import { OnyxInputLabelComponent } from '../../labels/onyx-input-label/onyx-input-label.component';
import type { OnyxTooltipContext } from '../../tooltip/interfaces';

export type OnyxUploadInput =
  OnyxMaybeArray<File> | OnyxMaybeArray<string> | null;

@Component({
  selector: 'onyx-upload',
  imports: [
    OnyxButtonComponent,
    OnyxInputLabelComponent,
    OnyxIconComponent,
    TranslatePipe,
    OnyxFileSizePipe,
    OnyxIconButtonComponent,
    NgTemplateOutlet,
    OnyxErrorComponent,
  ],
  providers: [
    {
      provide: OnyxFileSizePipe,
      useClass: OnyxFileSizePipe,
    },
    {
      provide: ONYX_FOCUSABLE,
      useExisting: forwardRef(() => OnyxUploadComponent),
    },
  ],
  templateUrl: './onyx-upload.component.html',
  styleUrl: './onyx-upload.component.scss',
})
export class OnyxUploadComponent
  extends OnyxBaseFormControlComponent
  implements FormValueControl<OnyxUploadInput | undefined>, OnInit
{
  private readonly translateService = inject(TranslateService);
  private readonly elementRef = inject(ElementRef<HTMLElement>);
  private readonly storageService = inject(OnyxStorageService);
  private readonly fileSizePipe = inject(OnyxFileSizePipe);
  private readonly renderer = inject(Renderer2);

  public readonly size = input<32 | 40 | 84 | 120>(40);
  public readonly value = model<OnyxUploadInput>();
  public readonly variant = input<'file' | 'image'>('file');
  public readonly label = input<string>();
  public readonly limit = input(1);
  public readonly columns = input<number>();
  public readonly hideBorder = input(false);
  public readonly hint = input<OnyxTooltipContext>();
  public readonly gap = input(16);

  protected readonly I18N = `${I18N_NAMESPACE}.upload`;
  protected readonly IMAGE_TYPES = [
    'image/avif',
    'image/bmp',
    'image/jpeg',
    'image/png',
    'image/svg+xml',
    'image/tiff',
    'image/webp',
  ];
  protected readonly MAX_IMAGE_SIZE = 1_048_576; // 1 mb
  protected readonly FILE_TYPES = [
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.oasis.opendocument.spreadsheet',
    'application/vnd.oasis.opendocument.text',
    'application/pdf',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ...this.IMAGE_TYPES,
  ];
  protected readonly MAX_FILE_SIZE = 8_388_608; // 8 mb
  protected inactive = signal(false);
  protected isDragOver = signal(false);
  protected fileBlob = signal<string | null>(null);
  protected files = signal<File[] | null>(null);
  protected error = signal<OnyxError | null>(null);
  protected loading = signal(false);
  protected isFile = computed(() => this.variant() === 'file');
  protected countLabel = computed(() => {
    const [fileCount, label] = [this.files()?.length, this.label()];
    if (!this.multiple() || !fileCount) return label;

    return `${label ? `${this.translateService.instant(label)} ` : ''}(${fileCount})`;
  });
  protected isReachedLimit = computed(() => {
    const fileCount = this.files()?.length;
    return fileCount === this.limit();
  });
  protected multiple = computed(() => this.limit() > 1);

  private inputElementRef =
    viewChild.required<ElementRef<HTMLInputElement>>('inputElement');

  constructor() {
    super();

    effect(() => {
      const element = this.elementRef.nativeElement as HTMLElement;
      const columns = this.columns() ?? this.limit();

      this.renderer.setStyle(
        element,
        'grid-template-columns',
        `repeat(${columns}, minmax(0, 1fr))`,
      );
      this.renderer.setStyle(
        element,
        'gap',
        `repeat(${columns}, minmax(0, 1fr))`,
      );
    });
  }

  public override ngOnInit(): void {
    super.ngOnInit();

    toObservable(this.value, { injector: this.injector })
      .pipe(
        filter((value) => value !== undefined),
        distinctUntilChanged(isEqual),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((value) => this.transformValue(value));
  }

  public focus(): void {
    if (this.disabled()) return;
    this.inputElementRef().nativeElement.click();
  }

  protected onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver.set(true);
  }

  protected onDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver.set(false);
  }

  protected onDrop(event: DragEvent): void {
    if (this.inactive()) return;

    event.preventDefault();
    this.isDragOver.set(false);
    const files = (event.dataTransfer?.files ?? []) as FileList;
    this.handleFiles(files);
  }

  protected onSpaceKeyPress(): void {
    this.inputElementRef().nativeElement.click();
  }

  protected onFileInputChange(event: Event): void {
    if (this.inactive()) return;

    const input = event.target as HTMLInputElement;
    const files = input.files!;
    this.handleFiles(files);
  }

  protected deleteFile(index = 0): void {
    if (this.disabled()) return;

    const files = this.files();

    if (this.multiple() && files) {
      files.splice(index, 1);
      const updatedFiles = files.length === 0 ? null : cloneDeep(files);
      this.files.set(updatedFiles);

      if (updatedFiles == null) this.resetFiles();

      if (!updatedFiles || updatedFiles.length < this.limit()) {
        this.inactive.set(false);
      }
    } else {
      this.resetFiles();
      this.error.set(null);
      this.inactive.set(false);
      if (this.inputElementRef) this.inputElementRef().nativeElement.value = '';
    }
  }

  protected onChangeFile(allowDisabled = true): void {
    if ((this.inactive() && !allowDisabled) || this.disabled()) return;

    this.inactive.set(false);
    this.touch.emit();
    setTimeout(() => {
      this.inputElementRef().nativeElement.click();
    });
  }

  protected previewFile(file: File) {
    this.storageService.showPreview(file);
  }

  protected getDisplayFileName(file: File): string {
    const extension = file.type.split('/').pop() ?? '';
    const label = this.translateService.instant(
      this.label() ?? `${this.I18N}.file`,
    );

    return file.name || `${label}.${extension}`;
  }

  private transformValue(value: OnyxUploadInput): void {
    this.error.set(null);

    if (value == null || (isArray(value) && !value.length)) {
      this.files.set(null);
      this.inactive.set(false);
      return;
    }

    if (isArray(value)) {
      if (!this.multiple()) {
        throw new Error('Unexpected array of files for limit equal to 1.');
      }

      if (value.every((item) => isString(item))) {
        this.loading.set(true);
        const files$ = value.map((file) => this.storageService.getFile(file));
        forkJoin(files$)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe((files) => this.setFiles(files))
          .add(() => this.loading.set(false));
      } else {
        this.files.set(value);
      }
    } else {
      if (this.multiple()) {
        throw new Error('Unexpected single file for limit greater than 1.');
      }

      if (isString(value)) {
        this.loading.set(true);
        this.storageService
          .getFile(value)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe((file) => this.setFiles([file]))
          .add(() => this.loading.set(false));
      } else {
        this.setFiles([value]);
      }
    }
  }

  private setFiles(files: File[]): void {
    files = files.slice(0, this.limit());
    this.files.set(files);

    if (!this.multiple()) {
      this.value.set(files[0]);
    } else {
      this.value.set(files);
    }

    if (!this.isFile()) {
      this.fileBlob.set(URL.createObjectURL(files[0]));
    }

    if (files.length === this.limit()) this.inactive.set(true);
  }

  private resetFiles(): void {
    const value = this.multiple() ? [] : null;

    this.files.set(null);
    this.value.set(value);
  }

  private handleInvalidFile(error: OnyxError): void {
    this.resetFiles();
    if (this.inputElementRef) this.inputElementRef().nativeElement.value = '';
    this.error.set(error);
  }

  private handleFiles(files: FileList): void {
    const convertedFiles = Array.from(files);
    this.error.set(null);

    if (this.isFile()) {
      for (const file of convertedFiles) {
        if (file.size === 0) {
          this.handleInvalidFile({ code: 'emptyFile' });
          return;
        } else if (file.size > this.MAX_FILE_SIZE) {
          this.handleInvalidFile({
            code: 'fileSize',
            data: {
              maxSize: this.fileSizePipe.transform(this.MAX_FILE_SIZE),
            },
          });
          return;
        }
      }
    } else {
      const file = files[0];
      this.fileBlob.set(URL.createObjectURL(file));

      if (
        !this.IMAGE_TYPES.includes(file.type) ||
        file.size === 0 ||
        file.size > this.MAX_IMAGE_SIZE
      ) {
        if (!this.IMAGE_TYPES.includes(file.type)) {
          this.handleInvalidFile({ code: 'invalidFileFormat' });
        } else if (file.size === 0) {
          this.handleInvalidFile({ code: 'emptyFile' });
        } else if (file.size > this.MAX_IMAGE_SIZE) {
          this.handleInvalidFile({
            code: 'fileSize',
            data: {
              maxSize: this.fileSizePipe.transform(this.MAX_IMAGE_SIZE),
            },
          });
        }
        return;
      }
    }

    if (!this.multiple()) {
      this.setFiles(convertedFiles);
    } else {
      this.setFiles([...(this.files() ?? []), ...convertedFiles]);
    }

    if (this.inputElementRef) this.inputElementRef().nativeElement.value = '';
  }
}
